import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Logo } from "@/components/site/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const http = axios.create({ baseURL: API, withCredentials: true });
const btn = "px-4 py-2 text-xs tracking-[0.2em] uppercase border transition-colors";

const errMsg = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg).join(" ");
  return "Errore. Riprova.";
};

const Login = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const { data } = await http.post("/auth/login", { email, password });
      onLogin(data);
    } catch (err) {
      setError(errMsg(err));
    }
  };
  const input = "w-full bg-transparent border-b border-[#d4a73c]/30 focus:border-[#e8c063] outline-none py-3 text-[#f4f1ea]";
  return (
    <form onSubmit={submit} className="max-w-sm w-full space-y-6 border border-[#d4a73c]/20 p-10" data-testid="admin-login-form">
      <h1 className="font-display text-4xl text-[#f4f1ea]">Area riservata</h1>
      <input type="email" required placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className={input} data-testid="admin-email-input" />
      <input type="password" required placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} className={input} data-testid="admin-password-input" />
      {error && <p className="text-sm text-red-400" data-testid="admin-login-error">{error}</p>}
      <button className="w-full bg-[#d4a73c] text-[#0d0d0e] py-4 text-xs tracking-[0.25em] uppercase hover:bg-[#e8c063]" data-testid="admin-login-btn">Accedi</button>
    </form>
  );
};

const RequestRow = ({ r, onStatus, onDelete }) => (
  <article data-testid={`request-${r.id}`} className={`border p-6 grid md:grid-cols-12 gap-4 ${r.status === "nuova" ? "border-[#d4a73c]/40" : "border-[#d4a73c]/10 opacity-70"}`}>
    <div className="md:col-span-3">
      <p className="font-display text-2xl text-[#f4f1ea]">{r.name}</p>
      <p className="text-xs text-[#a39d91] mt-1">{new Date(r.created_at).toLocaleString("it-IT")}</p>
      <span className="label !text-[10px] mt-2 inline-block">{r.status}</span>
    </div>
    <div className="md:col-span-3 text-sm space-y-1">
      <a href={`tel:${r.phone}`} className="block text-[#e8c063]">{r.phone}</a>
      <a href={`mailto:${r.email}`} className="block text-[#e9e4da] break-all">{r.email}</a>
      <p className="text-[#c9c3b7]">{r.service}</p>
    </div>
    <p className="md:col-span-4 text-sm text-[#d8d2c6] whitespace-pre-line">{r.message}</p>
    <div className="md:col-span-2 flex md:flex-col gap-2">
      <button data-testid={`request-toggle-${r.id}`} onClick={() => onStatus(r)} className={`${btn} border-[#d4a73c]/50 text-[#e8c063] hover:bg-[#d4a73c] hover:text-[#0d0d0e]`}>
        {r.status === "nuova" ? "Gestita" : "Riapri"}
      </button>
      <button data-testid={`request-delete-${r.id}`} onClick={() => onDelete(r)} className={`${btn} border-red-400/40 text-red-300 hover:bg-red-500/20`}>Elimina</button>
    </div>
  </article>
);

export default function Admin() {
  const [user, setUser] = useState(null);
  const [items, setItems] = useState([]);

  const load = useCallback(async () => {
    try {
      const { data } = await http.get("/contact");
      setItems(Array.isArray(data) ? data : []);
    } catch {
      setItems([]);
    }
  }, []);

  useEffect(() => {
    http.get("/auth/me").then(({ data }) => setUser(data)).catch(() => setUser(false));
  }, []);

  useEffect(() => {
    if (user) load();
  }, [user, load]);

  const onStatus = async (r) => {
    await http.patch(`/contact/${r.id}`, { status: r.status === "nuova" ? "gestita" : "nuova" });
    load();
  };
  const onDelete = async (r) => {
    if (!window.confirm(`Eliminare la richiesta di ${r.name}?`)) return;
    await http.delete(`/contact/${r.id}`);
    toast.success("Richiesta eliminata");
    load();
  };
  const logout = async () => {
    await http.post("/auth/logout");
    setUser(false);
  };

  return (
    <main className="min-h-screen bg-[#0d0d0e] text-[#f4f1ea]">
      <header className="border-b border-[#d4a73c]/15 px-6 lg:px-12 py-5 flex justify-between items-center">
        <a href="/" data-testid="admin-home-link"><Logo /></a>
        {user && <button onClick={logout} data-testid="admin-logout-btn" className={`${btn} border-[#d4a73c]/40 text-[#e8c063]`}>Esci</button>}
      </header>
      {user === null && <p className="p-12 text-[#c9c3b7]">Caricamento…</p>}
      {user === false && <div className="flex justify-center px-6 py-24"><Login onLogin={setUser} /></div>}
      {user && (
        <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-14">
          <p className="label">Richieste di contatto</p>
          <h1 className="font-display text-5xl mt-3" data-testid="admin-requests-count">
            {items.filter((i) => i.status === "nuova").length} nuove · {items.length} totali
          </h1>
          <div className="mt-10 space-y-4">
            {items.length === 0 && <p className="text-[#c9c3b7]" data-testid="admin-empty">Nessuna richiesta al momento.</p>}
            {items.map((r) => <RequestRow key={r.id} r={r} onStatus={onStatus} onDelete={onDelete} />)}
          </div>
        </section>
      )}
    </main>
  );
}
